import { Text } from "components/Typography/text.styled";
import { useTranslation } from "react-i18next";
import {
  PayoutContainer,
  PayoutDescriptionContainer,
  PayoutExployerTaxContainer,
  PayoutGrossContainer,
  PayoutPeriodContainer,
  PayoutRow,
  PayoutWorkerTaxContainer,
} from "./salary.styled";
import { FontFamily, FontSize } from "config/font";
import { Color } from "config/colors";
import { PayoutDetails, PayoutType } from "model/Salary";
import { formatTimePeriod } from "./individualSalary.component";
import { useCallback } from "react";

type Props = {
  payoutDetails: PayoutDetails[];
  currency: string;
};

export const PayoutDetailRow: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const getPayoutTypeText = useCallback(
    (type: string): string => {
      if (type === PayoutType.Monthly) {
        return t("Salary.Monthly");
      } else if (type === PayoutType.SimflexityPayMonthly) {
        return t("Salary.SimflexityPayMonthly");
      } else if (type === PayoutType.MonthlyPerHour) {
        return t("Salary.MonthlyPerHour");
      } else {
        return t("Salary.SimflexityPayAdvance");
      }
    },
    [t]
  );

  const renderPayoutRow = useCallback(
    (payDetail: PayoutDetails, index: number, total: number) => {
      const isLastRow = index === total - 1;

      return (
        <PayoutRow
          lastRow={isLastRow}
          key={`payout-${payDetail.period}-${index}`}
        >
          <PayoutDescriptionContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Type")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {getPayoutTypeText(payDetail.description)}
            </Text>
          </PayoutDescriptionContainer>
          <PayoutPeriodContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Period")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {formatTimePeriod(payDetail.period)}
            </Text>
          </PayoutPeriodContainer>
          <PayoutGrossContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.GrossPayoutAmount")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${payDetail.grossPayoutAmount} ${props.currency}`}
            </Text>
          </PayoutGrossContainer>
          <PayoutExployerTaxContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.EmployerTax")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${payDetail.employerTaxAmount} ${props.currency}`}
            </Text>
          </PayoutExployerTaxContainer>
          <PayoutWorkerTaxContainer>
            <Text
              style={{ margin: 0, padding: 0 }}
              fontSize={FontSize.Small}
              color={Color.SeaBlue600}
            >
              {t("Salary.Tax")}
            </Text>
            <Text
              color={Color.MidnightBlue}
              fontFamily={FontFamily.MontserratRegular}
            >
              {`${payDetail.workerTaxAmount} ${props.currency}`}
            </Text>
          </PayoutWorkerTaxContainer>
        </PayoutRow>
      );
    },
    [t, props.currency]
  );

  if (!props.payoutDetails || props.payoutDetails.length === 0) {
    return null;
  }

  return (
    <PayoutContainer>
      <Text fontFamily={FontFamily.MontserratSemiBold}>
        {t("Salary.PayoutDetails")}
      </Text>
      {props.payoutDetails.map((payDetail, index) =>
        renderPayoutRow(payDetail, index, props.payoutDetails.length)
      )}
    </PayoutContainer>
  );
};
